import React, { useState } from "react";
import { Button, Icon } from "../../";
import BusinessSelectionModal from "../BusinessSelectionModal";
import { usePostHog, useIntercom } from "@finitless/shared/hooks";
import { useTranslation } from "react-i18next";

const POSCard = ({ integration }) => {
  const { t } = useTranslation("integrations");
  const { name, logo, plugin, business_integrations = [] } = integration;
  const { isFeatureEnabled } = usePostHog();
  const { showMessenger } = useIntercom();
  const posConfigureEnabled = isFeatureEnabled("pos_configure_enabled");

  const [isBusinessModalOpen, setIsBusinessModalOpen] = useState(false);

  const connectedBusinessesCount = business_integrations.filter(
    (bi) =>
      bi.setup_status === "success" || (bi.enabled && bi.setup_status !== null),
  ).length;

  const pendingBusinessesCount = business_integrations.filter((bi) => {
    const isConnected =
      bi.setup_status === "success" || (bi.enabled && bi.setup_status !== null);
    return !isConnected;
  }).length;

  const isConnected = connectedBusinessesCount > 0;
  const hasPending = pendingBusinessesCount > 0;

  const handleConfigure = (e) => {
    e.stopPropagation();
    setIsBusinessModalOpen(true);
  };

  return (
    <div className="ordering-integration-card flex h-full flex-col rounded-lg border border-gray-800 bg-gray-900 p-6 transition-colors hover:bg-gray-800/50">
      {/* Header */}
      <div className="mb-4">
        <div className="mb-3 flex items-start justify-between">
          <div className="flex items-start space-x-3">
            {logo ? (
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-700 bg-gray-800">
                <img
                  src={logo}
                  alt={name}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
                <div className="flex hidden h-full w-full items-center justify-center bg-gray-700 text-xs font-semibold text-white">
                  {name.charAt(0)}
                </div>
              </div>
            ) : (
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg border border-gray-700 bg-gray-800">
                <Icon name="Package" className="h-6 w-6 text-gray-400" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="mb-1 text-lg font-semibold text-white">{name}</h3>
              {/* Description */}
              <p className="text-sm text-gray-400">
                {plugin?.description || "POS integration platform"}
              </p>
            </div>
          </div>
        </div>

        {(isConnected || hasPending) && (
          <div className="flex flex-col space-y-1">
            {isConnected && (
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-900/20 px-2 py-0.5 text-xs font-medium text-green-400">
                  Connected
                </span>
                <span className="text-xs text-gray-400">
                  {connectedBusinessesCount}{" "}
                  {connectedBusinessesCount === 1 ? "store" : "stores"}
                </span>
              </div>
            )}
            {hasPending && (
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center rounded-full border border-yellow-500/30 bg-yellow-900/20 px-2 py-0.5 text-xs font-medium text-yellow-400">
                  Pending
                </span>
                <span className="text-xs text-gray-400">
                  {pendingBusinessesCount}{" "}
                  {pendingBusinessesCount === 1 ? "store" : "stores"}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-auto space-y-2 border-t border-gray-800 pt-4">
        {posConfigureEnabled ? (
          <Button
            type="button"
            onClick={handleConfigure}
            emphasis="solid"
            intent="brand"
            size="touch"
            className="w-full"
          >
            <span>Configure</span>
          </Button>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              showMessenger();
            }}
            className="flex w-full items-center justify-center rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-600"
          >
            <Icon name="MessageCircle" className="mr-2 h-4 w-4" />
            <span>{t("contactUs")}</span>
          </button>
        )}
      </div>

      {/* Business Selection Modal */}
      <BusinessSelectionModal
        isOpen={isBusinessModalOpen}
        onClose={() => setIsBusinessModalOpen(false)}
        integration={integration}
      />
    </div>
  );
};

export default POSCard;
